import {Injectable} from '@angular/core';
import {SharedFacadeService} from '@shared/shared-facade.service';
import {filter, map, switchMap, take} from 'rxjs/operators';
import {MatSnackBar} from '@angular/material/snack-bar';
import {StudentView} from '@model/student-view';
import {combineLatest, Observable, of} from 'rxjs';
import {Store} from '@ngrx/store';
import {getAbsenceJustificationProposalsArray, getAbsenceJustificationProposalsTotalElements, getPlanningEvents, StoreState} from '@redux/reducers';
import {saveAs} from 'file-saver';
import {StudentLoadedAction} from '@redux/actions/students.action';
import {StudentDetail} from '@model/student-detail';
import {SchoolReportForStudent} from '@model/school-report-for-student';
import {SchoolReportType} from '@model/school-report-type';
import {School} from '@model/school';
import {schoolReportForStudentReset} from '@redux/actions/school-report.action';
import {StudentRemark} from '@model/student-remark';
import {Schedule} from '@model/schedule';
import {LegalStudentAbsence} from '@model/legal-student-absence';
import {StudentLateArrival} from '@model/student-late-arrival';
import {StudentEarlyDeparture} from '@model/student-early-departure';
import {StudentClassExclusion} from '@model/student-class-exclusion';
import {Profile} from '@model/profile';
import {AgendaActivity} from '@model/agenda-activity';
import {SchoolDiaryEntryEvent} from '@model/teacher-planning-event';
import {DateTime} from 'luxon';
import {SchoolReportSnapshots} from '@model/school-reports-snapshots';
import {EvaluationView} from '@model/evaluation';
import {EndOfYearDecision} from '@model/end-of-year-decision';
import {CertificationPerUnit} from '@model/certification-per-unit';
import {StudentService} from '@services/student.service';
import {ParentMeetingSlot} from '@model/parent-meeting-slot';
import {StudentAttribution} from '@model/student-attribution';
import {ParentMeetingService} from '@services/parent-meeting.service';
import {ParentMeetingSlotSaved} from '@redux/actions/parent-meeting.action';
import {PedagogicalFollowUpComment, PedagogicalFollowUpCommentsGroupByCat} from '@model/pedagogical-follow-up-comment';
import {SchoolSetting} from '@model/school-setting';
import {StudentDetention} from '@model/student-detention';
import {SharedDocument} from '@model/shared-documents';
import {UserGroups} from '@model/user-groups';
import {Holiday} from '@model/holiday';
import {StudentAbsenceJustificationProposal, StudentAbsenceJustificationProposalCreation} from '@model/student-absence-justification-proposal';
import {absenceJustificationProposalsLoaded, absenceJustificationProposalsReset} from '@redux/actions/absence-justification-proposal.action';

@Injectable()
export class ParentFacadeService {

  school$: Observable<School>;
  elementarySchool$: Observable<boolean>;
  schoolSettings$: Observable<SchoolSetting[]>;
  students$: Observable<StudentView[]>;
  selectedStudent$: Observable<StudentDetail>;
  schoolReportForStudent$: Observable<SchoolReportForStudent>;
  schoolReportTypesByClassId$: Observable<{ [classId: number]: SchoolReportType }>;
  studentRemarks$: Observable<StudentRemark[]>;
  studentAbsencesByDayByHour$: Observable<any[]>;
  schedule$: Observable<Schedule>;
  studentLegalAbsences$: Observable<LegalStudentAbsence[]>;
  absenceJustificationProposals$: Observable<StudentAbsenceJustificationProposal[]>;
  absenceJustificationProposalsTotalElements$: Observable<number>;
  studentLateArrivals$: Observable<StudentLateArrival[]>;
  studentEarlyDepartures$: Observable<StudentEarlyDeparture[]>;
  studentClassExclusions$: Observable<StudentClassExclusion[]>;
  profile$: Observable<Profile>;
  agendaActivities$: Observable<AgendaActivity[]>;
  isMobile$: Observable<boolean>;
  planningEventsByHoursByDates$: Observable<{ [hour: string]: { [date: string]: SchoolDiaryEntryEvent[] }; }>;
  planningEvents$: Observable<SchoolDiaryEntryEvent[]>;
  studentSchoolReportSnapshots$: Observable<SchoolReportSnapshots[]>;
  studentEvaluations$: Observable<EvaluationView[]>;
  studentEndOfYearDecision$: Observable<EndOfYearDecision>;
  certificationsPerUnit$: Observable<CertificationPerUnit[]>;
  certificationPerUnitForStudent$: Observable<CertificationPerUnit>;
  currentUserId$: Observable<number>;  parentMeetingSlots$: Observable<ParentMeetingSlot[]>;
  studentAttributions$: Observable<StudentAttribution[]>;
  pedagogicalFollowUpComments$: Observable<PedagogicalFollowUpComment[]>;
  pedagogicalFollowUpCommentsGroupByCat$: Observable<PedagogicalFollowUpCommentsGroupByCat[]>;
  studentDetentions$: Observable<StudentDetention[]>;
  sharedDocuments$: Observable<SharedDocument[]>;
  holidays$: Observable<Holiday[]>;
  holidaysForSelectedStudent$: Observable<Holiday[]>;

  constructor(private _sharedFacade: SharedFacadeService,
              private _studentService: StudentService,
              private _parentMeetingService: ParentMeetingService,
              private _snackBar: MatSnackBar,
              private _store: Store<StoreState>) {
    this.school$ = this._sharedFacade.school$;
    this.schoolSettings$ = this._sharedFacade.schoolSettings$;
    this.profile$ = this._sharedFacade.profile$;
    this.students$ = this._sharedFacade.studentSearchResult$.pipe(
      map(searchResult => searchResult.content)
    );
    this.selectedStudent$ = this._sharedFacade.selectedStudent$;
    this.elementarySchool$ = this._sharedFacade.elementarySchool$;
    this.schoolReportTypesByClassId$ = this._sharedFacade.schoolReportTypesByClassId$;
    this.schoolReportForStudent$ = this._sharedFacade.schoolReportForStudent$;
    this.studentRemarks$ = this._sharedFacade.studentRemarks$.pipe(map(page => page.content));
    this.studentAbsencesByDayByHour$ = this._sharedFacade.studentAbsencesByDayByHour$;
    this.schedule$ = this._sharedFacade.schedule$;
    this.studentLegalAbsences$ = this._sharedFacade.studentLegalAbsences$;
    this.absenceJustificationProposals$ = this._store.select(getAbsenceJustificationProposalsArray);
    this.absenceJustificationProposalsTotalElements$ = this._store.select(getAbsenceJustificationProposalsTotalElements);
    this.studentLateArrivals$ = this._sharedFacade.studentLateArrivals$;
    this.studentEarlyDepartures$ = this._sharedFacade.studentEarlyDepartures$;
    this.studentClassExclusions$ = this._sharedFacade.studentClassExclusions$;
    this.agendaActivities$ = this._sharedFacade.agendaActivities$;
    this.isMobile$ = this._sharedFacade.isMobile$;
    this.planningEventsByHoursByDates$ = this._sharedFacade.planningEventsByHoursByDates$;
    this.studentSchoolReportSnapshots$ = this._sharedFacade.studentSchoolReportSnapshots$;
    this.studentEvaluations$ = this._sharedFacade.studentEvaluations$;
    this.studentEndOfYearDecision$ = combineLatest([
      this._sharedFacade.studentEndOfYearDecisions$,
      this.selectedStudent$,
    ]).pipe(
      map(([endOfYearDecisions, selectedStudent]) => endOfYearDecisions.find(endOfYearDecision => endOfYearDecision?.studentId === selectedStudent?.id))
    );
    this.certificationsPerUnit$ = this._sharedFacade.certificationsPerUnit$;
    this.certificationPerUnitForStudent$ = this._sharedFacade.certificationPerUnitForStudent$;
    this.currentUserId$ = this.profile$.pipe(map(p => p.id));
    this.parentMeetingSlots$ = combineLatest([this._sharedFacade.parentMeetingSlots$, this.selectedStudent$]).pipe(
      filter(([parentMeetingSlots, selectedStudent]) => !!selectedStudent),
      map(([parentMeetingSlots, selectedStudent]) => {
        return parentMeetingSlots.map(parentMeetingSlot => {
          return this.obfuscationParentMeetingSlotAnotherChild(parentMeetingSlot, selectedStudent.id);
        })
      })
    );
    this.studentAttributions$ = this._sharedFacade.studentAttributions$;
    this.pedagogicalFollowUpComments$ = this._sharedFacade.pedagogicalFollowUpComments$;
    this.pedagogicalFollowUpCommentsGroupByCat$ = this._sharedFacade.pedagogicalFollowUpCommentsGroupByCat$;
    this.studentDetentions$ = this._sharedFacade.studentDetentions$;
    this.sharedDocuments$ = this._sharedFacade.sharedDocuments$.pipe(
      map(sharedDocuments => sharedDocuments.filter(sharedDocument => sharedDocument.permittedUserGroups.includes(UserGroups.PARENT)))
    );
    this.holidays$ = this._sharedFacade.holidays$;
    this.holidaysForSelectedStudent$ = this._sharedFacade.holidaysForSelectedStudent$;
    this.planningEvents$ = this._store.select(getPlanningEvents);
  }

  loadChildren() {
    this._sharedFacade.profile$.pipe(
      take(1)
    ).subscribe(profile => this._sharedFacade.loadChildren(profile.id));
  }

  selectStudent(student: StudentView) {
    this._store.dispatch(new StudentLoadedAction(student));
  }

  loadSchoolReportForStudent(studentId: number) {
    this._sharedFacade.loadSchoolReportForStudent(studentId);
  }

  schoolReportForStudentReset() {
    this._store.dispatch(schoolReportForStudentReset());
  }

  loadSchoolReportTypesByClassId() {
    this._sharedFacade.loadSchoolReportTypes();
    this._sharedFacade.loadSchoolReportTypeToClassAssignments();
  }

  resetSelectedStudent() {
    this._sharedFacade.resetSelectedStudent();
  }

  loadStudentRemarksForStudent(studentId: number) {
    this._sharedFacade.loadStudentRemarksForStudent(studentId);
  }

  loadStudentAbsencesForStudent(studentId: number) {
    this._sharedFacade.loadStudentAbsencesForStudent(studentId);
  }

  loadSchedule() {
    this._sharedFacade.loadSchedule();
  }

  loadHolidaysForCurrentSchoolYear() {
    this._sharedFacade.loadHolidaysForCurrentSchoolYear();
  }

  loadStudentLegalAbsencesForStudent(studentId: number) {
    this._sharedFacade.loadStudentLegalAbsencesForStudent(studentId);
  }

  loadAbsenceJustificationProposals(studentId: number) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._studentService.loadAbsenceJustificationProposalsForStudent(school.id, studentId))
    ).subscribe(page => this._store.dispatch(absenceJustificationProposalsLoaded({page})));
  }

  resetAbsenceJustificationProposals() {
    this._store.dispatch(absenceJustificationProposalsReset());
  }

  saveAbsenceJustificationProposal(studentId: number,
                                   proposalCreation: StudentAbsenceJustificationProposalCreation,
                                   onSaved: () => void) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._studentService.saveAbsenceJustificationProposal(school.id, studentId, proposalCreation))
    ).subscribe({
      next: () => {
        this._snackBar.open('Justificatif envoyé', null, {duration: 2000});
        onSaved();
      }
    });
  }

  loadStudentLateArrivalsForStudent(studentId: number) {
    this._sharedFacade.loadStudentLateArrivalsForStudent(studentId);
  }

  loadStudentEarlyDeparturesForStudent(studentId: number) {
    this._sharedFacade.loadStudentEarlyDeparturesForStudent(studentId);
  }

  loadStudentClassExclusionsForStudent(studentId: number) {
    this._sharedFacade.loadStudentClassExclusionsForStudent(studentId);
  }

  loadAgendaActivities() {
    this._sharedFacade.loadAgendaActivities();
  }

  loadSchoolDiary(startDate: DateTime, endDate: DateTime, studentId: number) {
    this._sharedFacade.loadSchoolDiaryForStudent(studentId, startDate, endDate);
  }

  loadStudentSchoolReportSnapshots(studentId: number) {
    this._sharedFacade.loadStudentSchoolReportSnapshots(studentId);
  }

  resetStudentReports() {
    this._sharedFacade.resetStudentReports();
  }

  downloadStudentSchoolReportSnapshot(studentReport: SchoolReportSnapshots) {
    this._sharedFacade.downloadStudentSchoolReportSnapshot(studentReport);
  }

  loadStudentEvaluationsForStudent(studentId: number) {
    this._sharedFacade.loadStudentEvaluationsForStudent(studentId);
  }

  loadStudentEndOfYearDecisionForStudent(studentId: number) {
    this._sharedFacade.loadStudentEndOfYearDecisionForStudent(studentId);
  }

  loadCertificationsPerUnit(studentId: number) {
    this._sharedFacade.loadCertificationsPerUnitForStudent(studentId);
  }

  loadCertificationPerUnitWithResultsForStudent(certificationPerUnitId: string, studentId: number) {
    this._sharedFacade.loadCertificationPerUnitWithResultsForStudent(certificationPerUnitId, studentId);
  }

  updateViewedByParent(remark: StudentRemark) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._studentService.updateRemarkViewed(school.id, remark)),
      switchMap(_ => this.selectedStudent$),
      take(1),
    ).subscribe(
      student => this.loadStudentRemarksForStudent(student.id)
    );
  }

  loadParentMeetingSlots(staffMemberId: number) {
    this._sharedFacade.loadParentMeetingSlots(staffMemberId);
  }

  loadStudentAttributions(studentId: number) {
    this._sharedFacade.loadStudentAttributions(studentId);
  }

  bookParentMeetingSlot(parentMeetingSlotId: string) {
    combineLatest([this.school$, this.selectedStudent$, this.profile$]).pipe(
      take(1),
      switchMap(([school, student, profile]) => {
        return this._parentMeetingService.bookParentMeetingSlot(school.id, parentMeetingSlotId, student.id).pipe(
          map(_ => ({
            id: parentMeetingSlotId,
            booked: true,
            concernedStudentFirstName: student.firstName,
            concernedStudentId: student.id,
            concernedStudentLastName: student.lastName,
            slotBookedByParentFirstName: profile.firstName,
            slotBookedByParentId: profile.id,
            slotBookedByParentLastName: profile.lastName
          }))
        )
      })
    ).subscribe(parentMeetingSlot => this._store.dispatch(ParentMeetingSlotSaved({parentMeetingSlot})));
  }

  unbookParentMeetingSlot(parentMeetingSlotId: string) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._parentMeetingService.unbookParentMeetingSlot(school.id, parentMeetingSlotId).pipe(
        map(_ => ({
          id: parentMeetingSlotId,
          booked: false,
          concernedStudentFirstName: null,
          concernedStudentId: null,
          concernedStudentLastName: null,
          slotBookedByParentFirstName: null,
          slotBookedByParentId: null,
          slotBookedByParentLastName: null,
        }))
      ))
    ).subscribe(parentMeetingSlot => this._store.dispatch(ParentMeetingSlotSaved({parentMeetingSlot})));
  }

  resetParentMeetingSlots() {
    this._sharedFacade.resetParentMeetingSlots();
  }

  private obfuscationParentMeetingSlotAnotherChild(parentMeetingSlot: ParentMeetingSlot, studentId: number): ParentMeetingSlot {
    return parentMeetingSlot.concernedStudentId === studentId ? parentMeetingSlot : {
      ...parentMeetingSlot,
      concernedStudentId: null,
      concernedStudentFirstName: null,
      concernedStudentLastName: null
    };
  }

  loadPedagogicalFollowUpComments(studentId: number) {
    this._sharedFacade.loadPedagogicalFollowUpComments(studentId);
  }

  loadStudentDetentionForStudent(studentId: number) {
    this._sharedFacade.loadStudentDetentionsByStudent(studentId);
  }

  loadSchoolSettings(): void {
    this._sharedFacade.loadSchoolSettings();
  }

  loadSharedDocuments(): void {
    this._sharedFacade.loadSharedDocuments();
  }

  downloadSharedDocument(sharedDocument: SharedDocument): void {
    this._sharedFacade.downloadSharedDocument(sharedDocument);
  }

  loadHorizonClassLinks(): void {
    this._sharedFacade.loadHorizonClassLinks();
  }

  downloadProposedJustificationDocument(proposal: StudentAbsenceJustificationProposal) {
      this._sharedFacade.downloadProposedJustificationDocument(proposal)
  }

  downloadLegalAbsenceDocument(studentId: number, justificationId: number, documentId: string) {
    this._sharedFacade.downloadLegalAbsenceDocument(studentId, justificationId, documentId);
  }
}
